import { useMemo, useState, type JSX } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Exercise, GradeResult } from '@yuan-math/exercises';
import {
  answerDiagnostic,
  progressOf,
  startDiagnostic,
  summarizeDiagnostic,
  type DiagnosticState,
  type DiagnosticSummary,
} from '@yuan-math/learning';
import { Button, Card, Mascot, MasteryBar } from '@yuan-math/ui';
import { ExerciseRunner } from '../components/ExerciseRunner.tsx';
import { bank, registry, useLearner } from '../state/LearnerContext.tsx';

/**
 * The adaptive placement test (§6, §27).
 *
 * The state machine lives in `@yuan-math/learning`; this page renders it. It
 * is available without an account on purpose — being able to find out where you
 * stand before signing up is the most persuasive thing the product does.
 *
 * **A question is held on screen after it is answered**, the way a boss fight
 * holds one (`BossPage`). This page used to be the only caller of
 * `ExerciseRunner` without `showNext`, and the effect was not that feedback was
 * suppressed but that it was *destroyed*: grading fired `answerDiagnostic`,
 * which produced the next question in the same commit, which changed
 * `exercise.id`, which fired the runner's reset effect and cleared the verdict
 * before a frame of it was painted. A student sat ten questions and was never
 * told whether they had got one of them right — in the one part of the platform
 * whose whole purpose is to tell them where they stand.
 *
 * The answer is `final` (see `ExerciseRunner`): the ladder has already recorded
 * the first answer and re-planned around it, so a second try could not change
 * anything, and the ordinary two-tries flow would make a student guess twice at
 * a question they have just discovered they cannot do.
 */
export function DiagnosticPage(): JSX.Element {
  const { signedIn, saveDiagnostic } = useLearner();
  const navigate = useNavigate();

  const options = useMemo(() => ({ registry, bank, seed: `dx-${Date.now().toString(36)}` }), []);
  const [state, setState] = useState<DiagnosticState | null>(null);
  const [summary, setSummary] = useState<DiagnosticSummary | null>(null);
  /**
   * The question still on screen, once it has been answered. Null while one is
   * being worked on, which is how the rest of this page tells "still answering"
   * from "answered, and reading why".
   */
  const [held, setHeld] = useState<Exercise | null>(null);

  /** Records an answer against the ladder, optionally keeping the question up. */
  const record = (result: GradeResult, hold: Exercise | null): void => {
    if (!state) return;
    const next = answerDiagnostic(state, result, options);
    setHeld(hold);
    setState(next);
    if (next.finished) setSummary(summarizeDiagnostic(next, options, Date.now()));
  };

  const handleGraded = (result: GradeResult, meta: { firstTry: boolean }): void => {
    if (result.status === 'malformed') return;
    // Belt and braces: `final` locks the runner after the first graded answer,
    // so there is no second one to ignore.
    if (!meta.firstTry) return;
    record(result, state?.currentExercise ?? null);
  };

  /*
   * "I don't know this yet" advances at once and holds nothing. There is no
   * verdict to read on a question the student did not answer, and leaving them
   * looking at it under a Next button would be the screen asking twice.
   */
  const skip = (): void => record({ status: 'incorrect', correct: false, expected: '', feedback: '' }, null);

  if (!state) {
    return (
      <div className="page page--narrow">
        <div className="session-done">
          <Mascot mood="thinking" size={88} />
        </div>
        <h1 className="page__title">Let's find your level.</h1>
        <p className="page__lead">
          Around ten questions, and they adapt as you go. Get one right and we skip everything underneath it; get one
          wrong and we look below it to find out why. There is no grade to declare and no time limit.
        </p>
        <Card className="diagnostic-intro">
          <ul className="prose-list">
            <li>Answer honestly — guessing right just sends you somewhere too hard.</li>
            <li>Each answer is marked right or wrong straight away, with the working, before the next question.</li>
            <li>If you have no idea, say so by skipping. That is real information too.</li>
            <li>It stops as soon as it knows, usually well before the tenth question.</li>
          </ul>
        </Card>
        <Button size="lg" onClick={() => setState(startDiagnostic(options))}>
          Begin
        </Button>
      </div>
    );
  }

  // The last question is read before the result card, like every other one.
  if (summary && held === null) {
    const level = registry.level(summary.recommendedLevel);
    const start = summary.startConceptId ? registry.concept(summary.startConceptId) : undefined;
    const startLesson = start ? registry.lessonsForConcept(start.id)[0] : undefined;

    /*
     * A strand the test never got to is named in a sentence rather than drawn
     * as an empty bar. Every strand is reported — leaving the unprobed ones off
     * the card entirely was how a profile came to consist of eleven bars all
     * reading exactly 100% — but a bar at zero says "you cannot do this", and
     * about a strand nobody asked about that is not a claim we are entitled to.
     */
    const strands = Object.entries(summary.strandScores);
    const reached = strands.filter(([id]) => (summary.strandCoverage?.[id] ?? 1) > 0);
    const unreached = strands.filter(([id]) => (summary.strandCoverage?.[id] ?? 1) === 0);

    return (
      <div className="page page--narrow">
        <h1 className="page__title">
          {summary.answeredEverythingCorrectly ? 'You answered everything we could ask.' : "We've found your starting point."}
        </h1>

        {summary.answeredEverythingCorrectly ? (
          <p className="page__lead">
            Nothing here was too hard for you. That means you are at least at the level below — not that this is
            your ceiling, only that it is as far as the published curriculum currently reaches.
          </p>
        ) : null}

        <Card className="diagnostic-result">
          <h2 className="diagnostic-result__heading">Your mathematics profile</h2>
          <p className="diagnostic-result__caption">
            How much of each strand this test was able to establish. A short test cannot reach everything, so a low bar
            often means we did not get there — not that you cannot do it.
          </p>
          <div className="diagnostic-result__bars">
            {reached.map(([strandId, score]) => (
              <MasteryBar key={strandId} value={score} label={registry.strand(strandId as never)?.title ?? strandId} />
            ))}
          </div>

          {unreached.length > 0 ? (
            <p className="diagnostic-result__unreached">
              <strong>Not covered by this test:</strong>{' '}
              {unreached.map(([strandId]) => registry.strand(strandId as never)?.title ?? strandId).join(', ')}.
            </p>
          ) : null}

          <dl className="diagnostic-result__facts">
            <div>
              <dt>Recommended level</dt>
              <dd>{level?.title ?? summary.recommendedLevel}</dd>
            </div>
            <div>
              <dt>Questions answered</dt>
              <dd>{state.askedConceptIds.length}</dd>
            </div>
            <div>
              <dt>Concepts credited</dt>
              <dd>{summary.assumedMastered.length}</dd>
            </div>
            {start ? (
              <div>
                <dt>Start here</dt>
                <dd>{start.title}</dd>
              </div>
            ) : null}
          </dl>
        </Card>

        <div className="button-row">
          {signedIn ? (
            <Button
              size="lg"
              onClick={() => {
                saveDiagnostic(summary);
                navigate(startLesson ? `/lessons/${startLesson.id}` : '/home');
              }}
            >
              Save this and start
            </Button>
          ) : (
            <Link to="/start" className="btn btn--primary btn--lg">
              Create a learner to save this
            </Link>
          )}
          {startLesson ? (
            <Link to={`/lessons/${startLesson.id}`} className="btn btn--secondary btn--lg">
              Open {start?.title}
            </Link>
          ) : null}
          <Link to="/map" className="btn btn--ghost">
            See it on the map
          </Link>
        </div>

        <p className="page__note">
          This is an estimate from a short test, not a verdict. Mastery is measured by what you actually do from here.
        </p>
      </div>
    );
  }

  const exercise = held ?? state.currentExercise;
  const progress = progressOf(state);
  // While a question is held, `asked` has already counted it — so it *is* the
  // number on screen rather than one behind it.
  const number = held ? progress.asked : progress.asked + 1;

  return (
    <div className="page page--narrow">
      <header className="practice-header">
        <div>
          <h1 className="page__title">Placement test</h1>
          <p className="practice-header__meta">
            Question {number} · adapting as you go
          </p>
        </div>
        <div className="diagnostic-progress">
          <div className="diagnostic-progress__bar" style={{ width: `${Math.round(progress.fraction * 100)}%` }} />
        </div>
      </header>

      {exercise ? (
        <>
          <ExerciseRunner
            exercise={exercise}
            onGraded={handleGraded}
            onNext={() => setHeld(null)}
            showNext
            final
            autoFocus
          />
          {held === null ? (
            <div className="practice-footer">
              <Button variant="ghost" size="sm" onClick={skip}>
                I don&apos;t know this yet
              </Button>
            </div>
          ) : null}
        </>
      ) : (
        <Card>
          <p>Working out where you stand…</p>
        </Card>
      )}
    </div>
  );
}
